import { useEffect } from 'react';
import { 
  SEOData, 
  DEFAULT_TITLE, 
  DEFAULT_DESCRIPTION, 
  DEFAULT_KEYWORDS 
} from '@shared/seo';

const DEFAULT_CANONICAL = 'https://www.talenture.ai/';

// Re-export for backward compatibility
export type { SEOData } from '@shared/seo';
export { generateJobSEO, generateJobListingSEO } from '@shared/seo';

export function useSEO(seoData: SEOData) {
  useEffect(() => {
    // Update page title
    if (seoData.title) {
      document.title = seoData.title;
    }

    // Create or update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
      }
      
      tag.content = content;
    };

    // Canonical link — update or create
    const updateCanonical = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.href = href;
    };

    const canonicalHref = seoData.canonical || window.location.href;
    updateCanonical(canonicalHref);

    // Basic meta tags
    if (seoData.description) {
      updateMetaTag('description', seoData.description);
    }

    if (seoData.keywords && seoData.keywords.length > 0) {
      updateMetaTag('keywords', seoData.keywords.join(', '));
    }

    // Open Graph tags
    if (seoData.ogTitle) {
      updateMetaTag('og:title', seoData.ogTitle, true);
    }

    if (seoData.ogDescription) {
      updateMetaTag('og:description', seoData.ogDescription, true);
    }

    if (seoData.ogImage) {
      updateMetaTag('og:image', seoData.ogImage, true);
    }

    if (seoData.ogUrl) {
      updateMetaTag('og:url', seoData.ogUrl, true);
    }

    // Always set these Open Graph tags
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', 'Talenture', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    if (seoData.ogTitle) {
      updateMetaTag('twitter:title', seoData.ogTitle);
    }
    if (seoData.ogDescription) {
      updateMetaTag('twitter:description', seoData.ogDescription);
    }
    if (seoData.ogImage) {
      updateMetaTag('twitter:image', seoData.ogImage);
    }

    // JSON-LD Structured Data
    if (seoData.structuredData) {
      // Remove existing structured data
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(seoData.structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      document.title = DEFAULT_TITLE;
      updateMetaTag('description', DEFAULT_DESCRIPTION);
      updateMetaTag('keywords', DEFAULT_KEYWORDS.join(', '));
      updateCanonical(DEFAULT_CANONICAL);
      
      // Remove job-specific structured data
      const structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (structuredDataScript) {
        structuredDataScript.remove();
      }
    };
  }, [seoData]);
}

// Re-export JobForSEO interface for backward compatibility
export type { JobForSEO } from '@shared/seo';